/**
 * <TranslatableText /> — wraps user-generated text with a "Translate" pill.
 *
 * Behaviour:
 *   • Renders `text` verbatim as `<div dir="auto">` so RTL scripts flow correctly.
 *   • Detects the visitor's browser language and offers to translate INTO it,
 *     unless the text is already suspected to be in that language (heuristic).
 *   • Caches the translation per (text, target) in-memory + sessionStorage so
 *     the same paragraph never hits the API twice in one visit.
 *   • On failure (429 / 503) shows a soft message and stays out of the way.
 *
 * Usage: <TranslatableText text={story.body} testid={`story-${i}`} />
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import { Languages, Loader2, RotateCcw, ChevronDown, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { getPreferredLang, PREF_LANG_EVENT } from "@/components/LanguageChip";

// Bulk page-translate wiring — the PageTranslateBar broadcasts these events
// and every TranslatableText translates in place.
const PAGE_TRANSLATE_EVENT = "translate-page";
const PAGE_REVERT_EVENT = "revert-page-translation";
const PAGE_TRANSLATE_STATUS_EVENT = "page-translate-status";
// Shared page-level counters so the bar shows a live progress indicator
// across every TranslatableText mounted on the page.
const pageState = { total: 0, pending: 0, completed: 0 };
function pingBar() {
  try {
    window.dispatchEvent(new CustomEvent(PAGE_TRANSLATE_STATUS_EVENT, { detail: { ...pageState } }));
  } catch { /* ignore */ }
}

// Simple in-memory cache shared across all component instances.
const memo = new Map(); // key: `${target}::${text}` → translated string
function getMemo(target, text) {
  const key = `${target}::${text}`;
  if (memo.has(key)) return memo.get(key);
  try {
    const s = sessionStorage.getItem(`i18n:${key}`);
    if (s) { memo.set(key, s); return s; }
  } catch { /* private-mode etc. */ }
  return null;
}
function setMemo(target, text, translated) {
  const key = `${target}::${text}`;
  memo.set(key, translated);
  try { sessionStorage.setItem(`i18n:${key}`, translated); } catch { /* ignore */ }
}

const LANG_NAMES = {
  en: "English", es: "Spanish", fr: "French", de: "German", it: "Italian",
  pt: "Portuguese", ru: "Russian", zh: "Chinese", ja: "Japanese", ko: "Korean",
  ar: "Arabic", hi: "Hindi", bn: "Bengali", ta: "Tamil", te: "Telugu",
  ml: "Malayalam", mr: "Marathi", gu: "Gujarati", pa: "Punjabi", kn: "Kannada",
  ur: "Urdu", he: "Hebrew", fa: "Persian", tr: "Turkish", nl: "Dutch",
  pl: "Polish", sv: "Swedish", el: "Greek", th: "Thai", vi: "Vietnamese",
  id: "Indonesian", ms: "Malay", sw: "Swahili", af: "Afrikaans", uk: "Ukrainian",
  cs: "Czech", ro: "Romanian", hu: "Hungarian", tl: "Filipino",
};

const POPULAR_TARGETS = ["en", "es", "fr", "hi", "ar", "zh", "pt", "ru", "de", "ja", "ko", "bn", "ur", "ta"];

function browserLang() {
  return getPreferredLang();
}

// Very light script detector — if any of the text is in a script strongly
// associated with a language, we assume the source and skip the translate
// prompt when browser lang matches.
function guessSourceScript(text) {
  if (!text) return null;
  if (/[\u0600-\u06FF]/.test(text)) return "ar";
  if (/[\u0590-\u05FF]/.test(text)) return "he";
  if (/[\u0900-\u097F]/.test(text)) return "hi";
  if (/[\u0980-\u09FF]/.test(text)) return "bn";
  if (/[\u0B80-\u0BFF]/.test(text)) return "ta";
  if (/[\u0C00-\u0C7F]/.test(text)) return "te";
  if (/[\u0D00-\u0D7F]/.test(text)) return "ml";
  if (/[\u4E00-\u9FFF]/.test(text)) return "zh";
  if (/[\u3040-\u30FF]/.test(text)) return "ja";
  if (/[\uAC00-\uD7AF]/.test(text)) return "ko";
  if (/[\u0400-\u04FF]/.test(text)) return "ru";
  if (/[\u0E00-\u0E7F]/.test(text)) return "th";
  return null;
}

export default function TranslatableText({ text, className = "", testid, minChars = 40 }) {
  const [target, setTarget] = useState(browserLang);
  useEffect(() => {
    const handler = () => setTarget(browserLang());
    window.addEventListener(PREF_LANG_EVENT, handler);
    return () => window.removeEventListener(PREF_LANG_EVENT, handler);
  }, []);
  const supported = LANG_NAMES[target] ? target : "en";
  const detectedScript = useMemo(() => guessSourceScript(text || ""), [text]);
  const looksSameLang = detectedScript && detectedScript === supported;
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [translated, setTranslated] = useState(() => (text && supported ? getMemo(supported, text) : null));
  const [activeTarget, setActiveTarget] = useState(translated ? supported : null);

  // Register with the page-level counter so <PageTranslateBar /> can show
  // progress. Only count blocks worth translating (skip tiny snippets).
  const isTranslatable = useMemo(
    () => !!(text && text.length >= minChars),
    [text, minChars],
  );
  useEffect(() => {
    if (!isTranslatable) return;
    pageState.total += 1;
    pingBar();
    return () => {
      pageState.total = Math.max(0, pageState.total - 1);
      pingBar();
    };
  }, [isTranslatable]);

  const doTranslate = useCallback(async (lang) => {
    if (!text) return;
    const cached = getMemo(lang, text);
    if (cached) { setTranslated(cached); setActiveTarget(lang); setShowPicker(false); return; }
    setBusy(true);
    if (isTranslatable) { pageState.pending += 1; pingBar(); }
    try {
      const { data } = await http.post("/i18n/translate", { text, target_lang: lang });
      if (data?.translated) {
        setMemo(lang, text, data.translated);
        setTranslated(data.translated);
        setActiveTarget(lang);
        if (isTranslatable) pageState.completed += 1;
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) toast.error("Translation is rate-limited — try again in a minute.");
      else if (status === 503) toast.error("Translation service is briefly unavailable.");
      else toast.error("Couldn't translate that.");
    } finally {
      setBusy(false);
      setShowPicker(false);
      if (isTranslatable) { pageState.pending = Math.max(0, pageState.pending - 1); pingBar(); }
    }
  }, [text, isTranslatable]);

  // Listen for the global page-translate broadcast from PageTranslateBar.
  // When fired, auto-translate this block into the requested language
  // (using the sessionStorage cache so we don't re-hit the API).
  useEffect(() => {
    if (!text) return undefined;
    const onTranslate = (e) => {
      const lang = e?.detail?.lang;
      if (!lang || lang === "en") return;
      // Skip when the source already appears to be in the target script.
      const src = guessSourceScript(text);
      if (src && src === lang) return;
      doTranslate(lang);
    };
    const onRevert = () => {
      setTranslated(null);
      setActiveTarget(null);
    };
    window.addEventListener(PAGE_TRANSLATE_EVENT, onTranslate);
    window.addEventListener(PAGE_REVERT_EVENT, onRevert);
    return () => {
      window.removeEventListener(PAGE_TRANSLATE_EVENT, onTranslate);
      window.removeEventListener(PAGE_REVERT_EVENT, onRevert);
    };
  }, [text, doTranslate]);

  // On first mount, if the user already had a page-wide translate active
  // (localStorage), auto-translate this new block too. This keeps the
  // selection persistent across route changes.
  useEffect(() => {
    if (!text) return;
    let stored = "";
    try { stored = localStorage.getItem("sp:page-translate-lang") || ""; } catch { /* ignore */ }
    if (!stored || stored === "en") return;
    const src = guessSourceScript(text);
    if (src && src === stored) return;
    // If we already have a translation cached, apply instantly.
    const cached = getMemo(stored, text);
    if (cached) {
      setTranslated(cached);
      setActiveTarget(stored);
    } else {
      doTranslate(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  // ---- Text-to-speech ----
  // Uses the browser's built-in SpeechSynthesis API — zero cost, works offline,
  // supports 30+ languages depending on the OS. When the browser doesn't have
  // a voice installed for the target language, we still play the text with the
  // default voice (correct pronunciation depends on the OS's language packs).
  const [speaking, setSpeaking] = useState(false);
  const utteranceRef = useRef(null);
  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  // Match an installed voice for the current language, if possible. Falls back
  // to the default voice — the utterance still plays and screen readers pick
  // up the `lang` attribute correctly.
  const pickVoice = (lang) => {
    if (!ttsSupported) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    const short = (lang || "").toLowerCase().split("-")[0];
    // Prefer exact match, then any voice starting with the same language code.
    return (
      voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(short + "-")) ||
      voices.find((v) => v.lang && v.lang.toLowerCase() === short) ||
      null
    );
  };

  const stopSpeaking = useCallback(() => {
    if (!ttsSupported) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setSpeaking(false);
  }, [ttsSupported]);

  const readAloud = useCallback(() => {
    if (!ttsSupported) {
      toast.error("Your browser doesn't support text-to-speech.");
      return;
    }
    // Toggle behaviour — a second click stops the current utterance.
    if (speaking) {
      stopSpeaking();
      return;
    }
    // Cancel anything else that may already be playing (e.g. from another card).
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const spoken = translated || text;
    const spokenLang = translated ? activeTarget : (guessSourceScript(text) || "en");
    const u = new SpeechSynthesisUtterance(spoken);
    u.lang = spokenLang || "en";
    u.rate = 0.98;
    u.pitch = 1;
    const voice = pickVoice(u.lang);
    if (voice) u.voice = voice;
    u.onstart = () => setSpeaking(true);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utteranceRef.current = u;
    try {
      window.speechSynthesis.speak(u);
    } catch (e) {
      toast.error("Couldn't play audio in this browser.");
      setSpeaking(false);
    }
  }, [ttsSupported, speaking, stopSpeaking, translated, text, activeTarget]);

  // Some browsers only load the voice list asynchronously — trigger a refetch.
  useEffect(() => {
    if (!ttsSupported) return;
    const ping = () => window.speechSynthesis.getVoices();
    ping();
    window.speechSynthesis.addEventListener("voiceschanged", ping);
    return () => {
      // Stop any in-flight speech if the component unmounts (avoids "ghost"
      // audio when navigating away).
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
      window.speechSynthesis.removeEventListener("voiceschanged", ping);
    };
  }, [ttsSupported]);

  if (!text) return null;
  const showToggle = !looksSameLang && (text.length >= minChars || !!translated);

  return (
    <div className={className} data-testid={testid}>
      <div dir="auto" className="whitespace-pre-wrap leading-relaxed">
        {translated || text}
      </div>
      {showToggle && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
          {translated ? (
            <>
              <span className="opacity-60">Translated to {LANG_NAMES[activeTarget] || activeTarget}.</span>
              <button
                type="button"
                onClick={() => { setTranslated(null); setActiveTarget(null); }}
                className="inline-flex items-center gap-1 opacity-70 hover:opacity-100 underline"
                data-testid={testid && `${testid}-show-original`}
              >
                <RotateCcw className="w-3 h-3" /> Show original
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => doTranslate(supported)}
              disabled={busy}
              className="inline-flex items-center gap-1 opacity-70 hover:opacity-100 underline disabled:opacity-50"
              title={`Translate to ${LANG_NAMES[supported] || supported}`}
              data-testid={testid && `${testid}-translate`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
              Translate{supported !== "en" ? ` to ${LANG_NAMES[supported] || supported}` : ""}
            </button>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPicker((s) => !s)}
              className="inline-flex items-center gap-0.5 opacity-60 hover:opacity-100"
              title="Choose language"
              data-testid={testid && `${testid}-lang-picker`}
            >
              <ChevronDown className="w-3 h-3" />
            </button>
            {showPicker && (
              <div
                className="absolute z-20 mt-1 rounded-lg shadow-lg border p-1 text-xs max-h-64 overflow-y-auto"
                style={{ background: "var(--wofy-paper)", borderColor: "var(--wofy-border)", minWidth: 160 }}
                onMouseLeave={() => setShowPicker(false)}
              >
                {POPULAR_TARGETS.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => doTranslate(code)}
                    className="w-full text-left px-2 py-1 rounded hover:bg-black/5"
                  >
                    {LANG_NAMES[code] || code}
                  </button>
                ))}
              </div>
            )}
          </div>
          {ttsSupported && (
            <button
              type="button"
              onClick={readAloud}
              className="inline-flex items-center gap-1 opacity-70 hover:opacity-100 ml-auto"
              title={speaking ? "Stop reading" : `Read aloud${translated ? ` in ${LANG_NAMES[activeTarget] || activeTarget}` : ""}`}
              aria-label={speaking ? "Stop reading" : "Read aloud"}
              data-testid={testid && `${testid}-read-aloud`}
            >
              {speaking ? (
                <>
                  <VolumeX className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
                  <span className="animate-pulse">Stop</span>
                </>
              ) : (
                <>
                  <Volume2 className="w-3.5 h-3.5" />
                  <span>Listen</span>
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
